import { useState, type FormEvent } from 'react';
import { useWhales } from '@/api/hooks';
import type { WhalesFilters, WhalesReport, WhalesSegment } from '@/api/types';
import { formatCount, formatDateTime, formatDay, formatEur, formatPercent } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Field, INPUT_CLASS, SELECT_CLASS } from '@/ui/Field';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { Stat } from '@/ui/Stat';
import { totalsOf } from './whale-figures';
import { WhalesTable } from './WhalesTable';

const LIMITS = [50, 100, 200, 500];
const DEFAULTS: WhalesFilters = { segment: 'potential-vip', from: '', to: '', limit: 50 };
const SEGMENTS: { value: WhalesSegment; label: string }[] = [
  { value: 'potential-vip', label: 'Potential VIP' },
  { value: 'vip', label: 'VIP' },
];
const segmentLabel = (segment: WhalesSegment) => SEGMENTS.find((s) => s.value === segment)?.label ?? segment;

// The segment's rule, as bigquery-service applies it.
function segmentRuleText({ segment, tier1Rule: rule, vipRule }: WhalesReport): string {
  const vipBar = `€${formatCount(vipRule.depositEurAbove)}`;
  if (segment === 'vip') return `VIP: all-time deposits above ${vipBar}, however many.`;
  return `Potential VIP: Tier 1 — ${rule.minDeposits}+ deposits totalling €${rule.minDepositEur}+ (€${rule.latamMinDepositEur}+ for LATAM players) in the ${rule.hours} hours from the first deposit, FTD included — and not VIP yet (all-time deposits up to ${vipBar}).`;
}

function ftdText({ from, to }: WhalesReport): string {
  if (from && to) return `FTD from ${formatDay(from)} to ${formatDay(to)} (UTC).`;
  if (from) return `FTD on or after ${formatDay(from)} (UTC).`;
  if (to) return `FTD on or before ${formatDay(to)} (UTC).`;
  return 'Every FTD.';
}

// ftdDays: the first FTD day the data holds, to its latest sync; bounds the date pickers.
function Filters({
  ftdDays,
  onApply,
}: {
  ftdDays: WhalesReport['activity']['ftd'];
  onApply(filters: WhalesFilters): void;
}) {
  const [draft, setDraft] = useState<WhalesFilters>(DEFAULTS);
  const set = (name: 'from' | 'to') => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [name]: event.target.value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply(draft);
  };

  const reset = () => {
    setDraft(DEFAULTS);
    onApply(DEFAULTS);
  };

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-4" aria-label="Filters">
      <Field id="whales-segment" label="Segment">
        <select
          id="whales-segment"
          className={SELECT_CLASS}
          value={draft.segment}
          onChange={(event) => setDraft((current) => ({ ...current, segment: event.target.value as WhalesSegment }))}
        >
          {SEGMENTS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field id="whales-from" label="FTD from (UTC day)" hint="Empty: from the first FTD.">
        <input
          id="whales-from"
          type="date"
          className={INPUT_CLASS}
          min={ftdDays?.from}
          max={ftdDays?.to}
          value={draft.from}
          onChange={set('from')}
        />
      </Field>
      <Field id="whales-to" label="FTD to (UTC day)" hint="Empty: up to the latest FTD.">
        <input
          id="whales-to"
          type="date"
          className={INPUT_CLASS}
          min={ftdDays?.from}
          max={ftdDays?.to}
          value={draft.to}
          onChange={set('to')}
        />
      </Field>
      <Field id="whales-limit" label="How many">
        <select
          id="whales-limit"
          className={SELECT_CLASS}
          value={draft.limit}
          onChange={(event) => setDraft((current) => ({ ...current, limit: Number(event.target.value) }))}
        >
          {LIMITS.map((limit) => (
            <option key={limit} value={limit}>
              Up to {limit}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex items-end gap-2 md:col-span-4">
        <Button type="submit">Apply</Button>
        <Button type="button" variant="secondary" onClick={reset}>
          Reset
        </Button>
      </div>
    </form>
  );
}

function Totals({ report }: { report: WhalesReport }) {
  const totals = totalsOf(report.rows);
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        label="Deposits"
        value={formatEur(totals.depositEur)}
        hint={`${formatCount(totals.depositCount)} deposits by ${formatCount(totals.players)} players`}
      />
      <Stat
        label="GGR"
        value={formatEur(totals.ggrEur)}
        hint={`Hold ${formatPercent(totals.holdRate)} · ${formatCount(totals.winners)} players won against the house`}
      />
      <Stat label="NGR" value={formatEur(totals.ngrEur)} hint="As the analysts' mart computes it" />
      <Stat
        label="Reward + cashback + bonus"
        value={formatEur(totals.rewardCostEur)}
        hint={
          totals.rewardShare === null
            ? 'No player with positive GGR'
            : `${formatPercent(totals.rewardShare)} of the GGR of players with positive GGR`
        }
      />
    </div>
  );
}

export function WhalesPage() {
  const [filters, setFilters] = useState<WhalesFilters>(DEFAULTS);
  const query = useWhales(filters);

  const report = query.data;
  const synced = report?.coverage ? report : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Whales</h1>
        <p className="text-sm text-slate-600">
          VIP players (all-time deposits above €1,000) and potential VIPs (Tier 1, judged by their first 72 hours from
          the first deposit), with what they deposited, played and cost us in rewards all time, and how CRM reaches
          them. Figures are the analysts&apos; mart in EUR, by UTC day, refreshed every 2 hours
          {report?.meta.lastSyncAt ? ` — last refreshed ${formatDateTime(report.meta.lastSyncAt)}` : ''}.
        </p>
      </div>

      <Card title="Filters">
        <Filters ftdDays={report?.activity.ftd ?? null} onApply={setFilters} />
      </Card>

      {query.isPending && <Loading what="whales" />}
      {query.isError && <ErrorNotice error={query.error} />}
      {report && !synced && (
        <Notice>
          The whales data has not been synced yet. It refreshes every 2 hours; check back after the next run.
        </Notice>
      )}

      {synced && (
        <>
          <Totals report={synced} />
          <Card
            title={`${segmentLabel(synced.segment)} players`}
            description={[
              ftdText(synced),
              synced.coverage ? `Money, bets and main game: all time, since ${formatDay(synced.coverage.from)}.` : '',
              synced.activity.comms
                ? `Last comm and preferred method: CRM messages since ${formatDay(synced.activity.comms.from)}.`
                : 'Last comm and preferred method: the CRM messages have not been synced yet.',
              segmentRuleText(synced),
              synced.rows.length === synced.limit
                ? `Showing the first ${synced.limit}; raise "How many" to see more.`
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <WhalesTable key={`${synced.segment}-${synced.from}-${synced.to}-${synced.limit}`} report={synced} />
          </Card>
        </>
      )}
    </div>
  );
}
